// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;
const REACTIONS = ['👍','❤️','😂','😮','😢','🔥'];

const formatTime = (d) => new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'});
const formatDate = (d) => {
  const date = new Date(d), today = new Date(), yest = new Date(today);
  yest.setDate(yest.getDate()-1);
  if (date.toDateString()===today.toDateString()) return 'Today';
  if (date.toDateString()===yest.toDateString())  return 'Yesterday';
  return date.toLocaleDateString('en-US',{day:'numeric',month:'long',year:'numeric'});
};
const getInitials = (n) => n ? n.split(' ').map(x=>x[0]).join('').toUpperCase().slice(0,2) : '?';
const isImage = t => t&&t.startsWith('image/');
const isVideo = t => t&&t.startsWith('video/');
const isAudio = t => t&&t.startsWith('audio/');

/* ── User Avatar (shows profile picture if set) ────────────────────────── */
const MsgAvatar = ({ msg, size='w-8 h-8' }) => {
  const [err, setErr] = useState(false);
  const src = !err && msg.profile_picture ? `${API}${msg.profile_picture}` : null;
  return (
    <div className={`${size} rounded-full overflow-hidden flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow`}
      style={{backgroundColor: src ? 'transparent' : (msg.avatar_color||'#25D366')}}>
      {src
        ? <img src={src} alt={msg.user_name} className="w-full h-full object-cover" onError={()=>setErr(true)}/>
        : getInitials(msg.user_name)}
    </div>
  );
};

/* ── File Attachment ───────────────────────────────────────────────────── */
const FileAttachment = ({fileUrl,fileName,fileType}) => {
  if (!fileUrl) return null;
  const src = `${API}${fileUrl}`;
  if (isImage(fileType)) return (
    <a href={src} target="_blank" rel="noopener noreferrer" className="block mt-1">
      <img src={src} alt={fileName||'image'} className="max-w-[200px] sm:max-w-xs rounded-lg shadow cursor-pointer hover:opacity-90" style={{maxHeight:220,objectFit:'cover'}}/>
    </a>
  );
  if (isVideo(fileType)) return (
    <video controls className="max-w-[200px] sm:max-w-xs rounded-lg shadow mt-1" style={{maxHeight:200}}>
      <source src={src} type={fileType}/> Your browser does not support video.
    </video>
  );
  if (isAudio(fileType)) return (
    <audio controls className="mt-1 w-full max-w-[200px] sm:max-w-xs">
      <source src={src} type={fileType}/>
    </audio>
  );
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" download={fileName}
      className="flex items-center gap-2 mt-1 bg-black/10 hover:bg-black/20 rounded-lg px-3 py-2 transition-colors">
      <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
      </svg>
      <div className="min-w-0"><p className="text-sm font-medium truncate">{fileName||'File'}</p><p className="text-xs opacity-60">Download</p></div>
    </a>
  );
};

/* ── Call Overlay ─────────────────────────────────────────────────────── */
const CallOverlay = ({call,onAccept,onReject,onEnd,localStream,remoteStream,callStatus}) => {
  const localRef = useRef(null), remoteRef = useRef(null);
  useEffect(()=>{ if(localRef.current&&localStream) localRef.current.srcObject=localStream; },[localStream]);
  useEffect(()=>{ if(remoteRef.current&&remoteStream) remoteRef.current.srcObject=remoteStream; },[remoteStream]);
  if(!call) return null;
  if(callStatus==='incoming') return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-whatsapp-darker rounded-2xl p-8 text-center shadow-2xl max-w-xs w-full mx-4">
        <div className="w-20 h-20 bg-whatsapp-green rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <span className="text-white text-3xl font-bold">{getInitials(call.callerName)}</span>
        </div>
        <p className="text-white text-xl font-bold mb-1">{call.callerName}</p>
        <p className="text-green-300 text-sm mb-6">Incoming {call.callType} call…</p>
        <div className="flex justify-center gap-6">
          <button onClick={onReject} className="w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors" title="Decline">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
          </button>
          <button onClick={onAccept} className="w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-colors" title="Accept">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
  if(callStatus==='calling') return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-whatsapp-darker rounded-2xl p-8 text-center shadow-2xl max-w-xs w-full mx-4">
        <div className="w-20 h-20 bg-whatsapp-green rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <span className="text-white text-3xl font-bold">S3</span>
        </div>
        <p className="text-white text-xl font-bold mb-1">Group S3</p>
        <p className="text-green-300 text-sm mb-6">Calling…</p>
        <button onClick={onEnd} className="w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg mx-auto transition-colors">
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
        </button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex-1 relative bg-gray-900 flex items-center justify-center">
        {call.callType==='video' ? <video ref={remoteRef} autoPlay playsInline className="w-full h-full object-cover"/> : (
          <div className="text-center"><div className="w-24 h-24 bg-whatsapp-green rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-white text-4xl font-bold">{getInitials(call.callerName||'S3')}</span></div><p className="text-white text-xl font-semibold">{call.callerName||'Group S3'}</p><p className="text-green-300 mt-1">Call in progress…</p></div>
        )}
        {call.callType==='video' && <video ref={localRef} autoPlay playsInline muted className="absolute bottom-4 right-4 w-28 h-20 object-cover rounded-xl border-2 border-white shadow-lg"/>}
      </div>
      <div className="bg-gray-900 py-6 flex justify-center">
        <button onClick={onEnd} className="w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors">
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
        </button>
      </div>
    </div>
  );
};

/* ── Main Chat ────────────────────────────────────────────────────────── */
const Chat = () => {
  const { user, token } = useAuth();
  const [messages, setMessages]         = useState([]);
  const [newMessage, setNewMessage]     = useState('');
  const [loading, setLoading]           = useState(true);
  const [sending, setSending]           = useState(false);
  const [replyingTo, setReplyingTo]     = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview]   = useState(null);
  const [toast, setToast]               = useState(null);
  const [showReactions, setShowReactions] = useState(null); // messageId
  const [onlineUsers, setOnlineUsers]   = useState([]);
  const [callStatus, setCallStatus]     = useState(null);
  const [currentCall, setCurrentCall]   = useState(null);
  const [localStream, setLocalStream]   = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const messagesEndRef = useRef(null);
  const socketRef      = useRef(null);
  const inputRef       = useRef(null);
  const fileInputRef   = useRef(null);
  const pcRef          = useRef(null);
  const localStreamRef = useRef(null);
  const currentCallRef = useRef(null);
  const callStatusRef  = useRef(null);

  useEffect(()=>{ localStreamRef.current=localStream; },[localStream]);
  useEffect(()=>{ currentCallRef.current=currentCall; },[currentCall]);
  useEffect(()=>{ callStatusRef.current=callStatus; },[callStatus]);

  const showToast = useCallback((type,msg)=>{ setToast({type,msg}); setTimeout(()=>setToast(null),4000); },[]);
  const scrollToBottom = useCallback(()=>{ messagesEndRef.current?.scrollIntoView({behavior:'smooth'}); },[]);

  const fetchMessages = useCallback(async()=>{
    try {
      const res = await axios.get(`${API}/api/messages`,{headers:{Authorization:`Bearer ${token}`}});
      setMessages(res.data);
    } catch(err){ console.error(err); }
    finally{ setLoading(false); }
  },[token]);

  useEffect(()=>{ fetchMessages(); },[fetchMessages]);
  useEffect(()=>{ scrollToBottom(); },[messages,scrollToBottom]);

  /* ── Greeting notification on mount ──────────────────────── */
  useEffect(()=>{
    const h = new Date().getHours();
    const greet = h>=5&&h<12?`Good morning, ${user?.name?.split(' ')[0]}! ☀️`
                : h>=12&&h<17?`Good afternoon, ${user?.name?.split(' ')[0]}! 🌤️`
                : h>=17&&h<21?`Good evening, ${user?.name?.split(' ')[0]}! 🌆`
                : `Good night, ${user?.name?.split(' ')[0]}! 🌙`;
    const t = setTimeout(()=>showToast('success',greet),600);
    return ()=>clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  /* ── WebRTC helpers ───────────────────────────────────────── */
  const createPeerConnection = useCallback((peerId)=>{
    if(pcRef.current){ pcRef.current.close(); pcRef.current=null; }
    const pc = new RTCPeerConnection({iceServers:[{urls:'stun:stun.l.google.com:19302'},{urls:'stun:stun1.l.google.com:19302'}]});
    pc.onicecandidate = e=>{ if(e.candidate&&socketRef.current) socketRef.current.emit('webrtc:ice',{to:peerId,candidate:e.candidate}); };
    pc.ontrack = e=>{ if(e.streams?.[0]) setRemoteStream(e.streams[0]); };
    pc.onconnectionstatechange = ()=>{ if(pc.connectionState==='disconnected'||pc.connectionState==='failed') stopCall(); };
    pcRef.current=pc; return pc;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const stopCall = useCallback(()=>{
    if(pcRef.current){ pcRef.current.close(); pcRef.current=null; }
    localStreamRef.current?.getTracks().forEach(t=>t.stop());
    setLocalStream(null); setRemoteStream(null); setCallStatus(null); setCurrentCall(null);
  },[]);

  const startCall = useCallback(async(callType)=>{
    if(callStatusRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(callType==='video'?{video:true,audio:true}:{audio:true});
      setLocalStream(stream);
      const callData={callerId:user.id,callerName:user.name,callType};
      setCurrentCall(callData); setCallStatus('calling');
      socketRef.current.emit('call:start',callData);
    } catch(err){ showToast('error','Camera/mic permission denied.'); }
  },[user,showToast]);

  const acceptCall = useCallback(async()=>{
    const call=currentCallRef.current; if(!call) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(call.callType==='video'?{video:true,audio:true}:{audio:true});
      setLocalStream(stream);
      const pc=createPeerConnection(call.socketId);
      stream.getTracks().forEach(t=>pc.addTrack(t,stream));
      socketRef.current.emit('call:accept',{to:call.socketId,calleeId:user.id,calleeName:user.name});
      setCallStatus('active');
    } catch(err){ showToast('error','Camera/mic permission denied.'); }
  },[user,showToast,createPeerConnection]);

  const rejectCall = useCallback(()=>{
    const call=currentCallRef.current;
    if(call?.socketId) socketRef.current.emit('call:reject',{to:call.socketId});
    stopCall();
  },[stopCall]);

  const endCall = useCallback(()=>{
    const call=currentCallRef.current;
    if(call?.socketId) socketRef.current.emit('call:end',{to:call.socketId});
    else socketRef.current.emit('call:cancel');
    stopCall();
  },[stopCall]);

  /* ── Socket.io ────────────────────────────────────────────── */
  useEffect(()=>{
    const socket=io(API,{transports:['polling','websocket'],reconnection:true,reconnectionAttempts:10,reconnectionDelay:1000,timeout:20000});
    socketRef.current=socket;
    socket.emit('user:online',{userId:user.id,name:user.name});
    socket.on('online:update',users=>setOnlineUsers(users));
    socket.on('new_message',msg=>setMessages(prev=>prev.find(m=>m.id===msg.id)?prev:[...prev,{...msg,replies:msg.replies||[],reactions:msg.reactions||[]}]));
    socket.on('new_reply',reply=>setMessages(prev=>prev.map(m=>m.id===reply.message_id?{...m,replies:[...(m.replies||[]),reply]}:m)));
    socket.on('message:deleted',({messageId})=>setMessages(prev=>prev.filter(m=>m.id!==messageId)));
    socket.on('message:reactions',({messageId,reactions})=>setMessages(prev=>prev.map(m=>m.id===messageId?{...m,reactions}:m)));
    socket.on('call:incoming',data=>{ setCurrentCall(data); setCallStatus('incoming'); });
    socket.on('call:accepted',async data=>{
      setCallStatus('active');
      const pc=createPeerConnection(data.socketId);
      localStreamRef.current?.getTracks().forEach(t=>pc.addTrack(t,localStreamRef.current));
      const offer=await pc.createOffer(); await pc.setLocalDescription(offer);
      socket.emit('webrtc:offer',{to:data.socketId,offer});
    });
    socket.on('call:rejected',()=>{ showToast('error','Call declined.'); stopCall(); });
    socket.on('call:cancelled',()=>stopCall());
    socket.on('call:ended',()=>stopCall());
    socket.on('webrtc:offer',async({offer,from})=>{
      const pc=pcRef.current; if(!pc) return;
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer=await pc.createAnswer(); await pc.setLocalDescription(answer);
      socket.emit('webrtc:answer',{to:from,answer});
    });
    socket.on('webrtc:answer',async({answer})=>{ const pc=pcRef.current; if(pc) await pc.setRemoteDescription(new RTCSessionDescription(answer)); });
    socket.on('webrtc:ice',async({candidate})=>{ const pc=pcRef.current; if(pc) try{ await pc.addIceCandidate(new RTCIceCandidate(candidate)); }catch(e){} });
    return ()=>socket.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  /* ── Send message ─────────────────────────────────────────── */
  const handleSend = async(e)=>{
    e.preventDefault();
    if((!newMessage.trim()&&!selectedFile)||sending) return;
    setSending(true);
    try {
      if(selectedFile){
        const fd=new FormData(); fd.append('file',selectedFile);
        if(newMessage.trim()) fd.append('content',newMessage.trim());
        await axios.post(`${API}/api/messages/upload`,fd,{headers:{Authorization:`Bearer ${token}`,'Content-Type':'multipart/form-data'}});
      } else {
        await axios.post(`${API}/api/messages`,{content:newMessage.trim()},{headers:{Authorization:`Bearer ${token}`}});
      }
      setNewMessage(''); setSelectedFile(null); setFilePreview(null);
      inputRef.current?.focus();
    } catch(err){ showToast('error',err.response?.data?.message||'Failed to send.'); }
    finally{ setSending(false); }
  };

  /* ── Send reply ───────────────────────────────────────────── */
  const handleReply = async(msgId)=>{
    if(!replyContent.trim()||sendingReply) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/api/messages/${msgId}/reply`,{content:replyContent.trim()},{headers:{Authorization:`Bearer ${token}`}});
      setReplyContent(''); setReplyingTo(null);
    } catch(err){ console.error(err); }
    finally{ setSendingReply(false); }
  };

  /* ── Delete message ───────────────────────────────────────── */
  const handleDelete = async(msgId)=>{
    if(!window.confirm('Delete this message?')) return;
    try { await axios.delete(`${API}/api/messages/${msgId}`,{headers:{Authorization:`Bearer ${token}`}}); }
    catch(err){ showToast('error','Could not delete message.'); }
  };

  /* ── React to message ─────────────────────────────────────── */
  const handleReact = async(msgId,emoji)=>{
    setShowReactions(null);
    try { await axios.post(`${API}/api/messages/${msgId}/react`,{emoji},{headers:{Authorization:`Bearer ${token}`}}); }
    catch(err){ console.error(err); }
  };

  const handleFileChange = e=>{
    const file=e.target.files[0]; if(!file) return;
    setSelectedFile(file);
    if(file.type.startsWith('image/')){ const r=new FileReader(); r.onloadend=()=>setFilePreview(r.result); r.readAsDataURL(file); }
    else setFilePreview(null);
    e.target.value='';
  };

  const grouped = messages.reduce((g,m)=>{ const d=formatDate(m.created_at); if(!g[d])g[d]=[]; g[d].push(m); return g; },{});

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div className="flex bg-whatsapp-bg" style={{height:'calc(100vh - 56px)'}}>

      <CallOverlay call={currentCall} callStatus={callStatus} localStream={localStream} remoteStream={remoteStream} onAccept={acceptCall} onReject={rejectCall} onEnd={endCall}/>

      {/* Toast */}
      {toast&&(
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-white text-sm font-medium max-w-[90vw] ${toast.type==='error'?'bg-red-500':'bg-whatsapp-green'}`}>
          <span>{toast.type==='error'?'⚠️':'✅'}</span>
          <span>{toast.msg}</span>
          <button onClick={()=>setToast(null)} className="opacity-70 hover:opacity-100 ml-1">✕</button>
        </div>
      )}

      {/* ── Main chat column ── */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="bg-whatsapp-darker text-white px-3 sm:px-4 py-2.5 flex items-center gap-3 shadow-md flex-shrink-0">
          <div className="w-9 h-9 bg-whatsapp-green rounded-full flex items-center justify-center shadow flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/></svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-base leading-tight">Group S3</h2>
            <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"/><span className="text-green-300 text-xs">{onlineUsers.length} online</span></div>
          </div>
          <button onClick={()=>startCall('audio')} disabled={!!callStatus} className="p-1.5 rounded-full hover:bg-whatsapp-dark transition-colors disabled:opacity-40" title="Audio call">
            <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
          </button>
          <button onClick={()=>startCall('video')} disabled={!!callStatus} className="p-1.5 rounded-full hover:bg-whatsapp-dark transition-colors disabled:opacity-40" title="Video call">
            <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 24 24"><path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"/></svg>
          </button>
          <button onClick={()=>fetchMessages()} className="p-1.5 rounded-full hover:bg-whatsapp-dark transition-colors" title="Refresh">
            <svg className="w-5 h-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-3 space-y-1" onClick={()=>setShowReactions(null)}
          style={{backgroundImage:"url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23128C7E' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}>
          {loading ? (
            <div className="flex items-center justify-center h-full"><div className="text-center"><svg className="animate-spin w-10 h-10 text-whatsapp-green mx-auto mb-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg><p className="text-gray-500 text-sm">Loading…</p></div></div>
          ) : messages.length===0 ? (
            <div className="flex items-center justify-center h-full"><div className="text-center bg-white rounded-2xl p-8 shadow-md max-w-xs"><div className="text-5xl mb-3">💬</div><h3 className="text-gray-700 font-bold mb-1">No messages yet</h3><p className="text-gray-500 text-sm">Be the first to write!</p></div></div>
          ) : (
            Object.entries(grouped).map(([date,msgs])=>(
              <div key={date}>
                <div className="flex items-center justify-center my-3"><span className="bg-white/80 text-gray-500 text-xs px-3 py-1 rounded-full shadow-sm backdrop-blur-sm">{date}</span></div>
                {msgs.map(msg=>{
                  const isOwn=msg.user_id===user.id;
                  return (
                    <div key={msg.id} className={`flex mb-2 group ${isOwn?'justify-end':'justify-start'}`}>
                      <div className={`max-w-[78vw] sm:max-w-xs md:max-w-md lg:max-w-lg flex flex-col ${isOwn?'items-end':'items-start'}`}>
                        <div className={`flex items-end gap-1.5 ${isOwn?'flex-row-reverse':''}`}>
                          {/* Avatar with profile picture */}
                          {!isOwn && <MsgAvatar msg={msg} size="w-7 h-7 sm:w-8 sm:h-8"/>}
                          <div>
                            {!isOwn&&<p className="text-xs font-semibold text-whatsapp-dark mb-1 ml-1">{msg.user_name}</p>}
                            {/* Bubble */}
                            <div className={`relative px-3 py-2 rounded-2xl shadow-sm ${isOwn?'bg-whatsapp-light text-gray-800 rounded-tr-sm':'bg-white text-gray-800 rounded-tl-sm'}`}>
                              {msg.file_url&&<FileAttachment fileUrl={msg.file_url} fileName={msg.file_name} fileType={msg.file_type}/>}
                              {msg.content&&<p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>}
                              <div className={`flex items-center mt-1 gap-1 ${isOwn?'justify-end':'justify-start'}`}>
                                <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                                {isOwn&&<svg className="w-3 h-3 text-whatsapp-dark" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                              </div>
                            </div>

                            {/* Reactions display */}
                            {msg.reactions?.length>0&&(
                              <div className={`flex flex-wrap gap-1 mt-1 ${isOwn?'justify-end':'justify-start'}`}>
                                {msg.reactions.map(r=>(
                                  <button key={r.emoji} onClick={e=>{e.stopPropagation();handleReact(msg.id,r.emoji);}}
                                    className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-all ${r.reacted_by_me?'bg-whatsapp-green/20 border-whatsapp-green text-whatsapp-dark':'bg-white border-gray-200 text-gray-600 hover:border-whatsapp-green'}`}>
                                    <span>{r.emoji}</span><span className="font-medium">{r.count}</span>
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* Action row */}
                            <div className={`flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isOwn?'justify-end':'justify-start'}`}>
                              {/* React */}
                              <div className="relative">
                                <button onClick={e=>{e.stopPropagation();setShowReactions(showReactions===msg.id?null:msg.id);}}
                                  className="text-xs text-gray-400 hover:text-yellow-500 transition-colors p-1 rounded-lg hover:bg-gray-100" title="React">
                                  😊
                                </button>
                                {showReactions===msg.id&&(
                                  <div className={`absolute bottom-7 ${isOwn?'right-0':'left-0'} bg-white rounded-2xl shadow-xl border border-gray-100 px-2 py-1.5 flex gap-1.5 z-30`} onClick={e=>e.stopPropagation()}>
                                    {REACTIONS.map(em=>(
                                      <button key={em} onClick={()=>handleReact(msg.id,em)} className="text-lg hover:scale-125 transition-transform p-0.5">{em}</button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {/* Reply */}
                              <button onClick={()=>{setReplyingTo(replyingTo===msg.id?null:msg.id);setReplyContent('');}}
                                className="text-xs text-gray-400 hover:text-whatsapp-green transition-colors p-1 rounded-lg hover:bg-gray-100" title="Reply">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>
                              </button>
                              {/* Delete */}
                              {(isOwn||user.role==='admin')&&(
                                <button onClick={()=>handleDelete(msg.id)}
                                  className="text-xs text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-gray-100" title="Delete">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                </button>
                              )}
                            </div>

                            {/* Replies */}
                            {msg.replies?.length>0&&(
                              <div className={`mt-1.5 space-y-1 border-l-2 border-whatsapp-green/30 pl-2 ${isOwn?'items-end':'items-start'}`}>
                                {msg.replies.map(r=>(
                                  <div key={r.id} className="bg-white/70 rounded-xl px-2.5 py-1.5 text-xs shadow-sm">
                                    <span className="font-semibold text-whatsapp-dark">{r.user_name}: </span>
                                    <span className="text-gray-700">{r.content}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Reply input */}
                            {replyingTo===msg.id&&(
                              <div className="mt-2 flex items-center gap-2">
                                <input value={replyContent} onChange={e=>setReplyContent(e.target.value)}
                                  onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&handleReply(msg.id)}
                                  placeholder="Write a reply…"
                                  className="flex-1 text-xs bg-white border border-gray-200 rounded-full px-3 py-1.5 outline-none focus:ring-1 focus:ring-whatsapp-green min-w-0"/>
                                <button onClick={()=>handleReply(msg.id)} disabled={sendingReply||!replyContent.trim()}
                                  className="text-whatsapp-green disabled:opacity-40 flex-shrink-0">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
          <div ref={messagesEndRef}/>
        </div>

        {/* Input area */}
        <div className="bg-white border-t border-gray-200 px-2 sm:px-3 py-2 flex-shrink-0">
          {filePreview&&(
            <div className="flex items-center gap-2 mb-2 bg-gray-50 rounded-xl px-3 py-2">
              <img src={filePreview} alt="preview" className="w-10 h-10 rounded-lg object-cover"/>
              <span className="text-xs text-gray-600 flex-1 truncate">{selectedFile?.name}</span>
              <button onClick={()=>{setSelectedFile(null);setFilePreview(null);}} className="text-red-400 hover:text-red-600 text-xs font-bold">✕</button>
            </div>
          )}
          {selectedFile&&!filePreview&&(
            <div className="flex items-center gap-2 mb-2 bg-gray-50 rounded-xl px-3 py-2">
              <span className="text-lg">📎</span>
              <span className="text-xs text-gray-600 flex-1 truncate">{selectedFile.name}</span>
              <button onClick={()=>{setSelectedFile(null);setFilePreview(null);}} className="text-red-400 hover:text-red-600 text-xs font-bold">✕</button>
            </div>
          )}
          <form onSubmit={handleSend} className="flex items-end gap-2">
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"/>
            <button type="button" onClick={()=>fileInputRef.current?.click()}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-whatsapp-green hover:bg-gray-100 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
            </button>
            <input ref={inputRef} type="text" value={newMessage} onChange={e=>setNewMessage(e.target.value)}
              onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); handleSend(e); } }}
              placeholder="Message…"
              className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-whatsapp-green focus:bg-white transition-all min-w-0"/>
            <button type="submit" disabled={sending||(!newMessage.trim()&&!selectedFile)}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-whatsapp-green hover:bg-whatsapp-dark disabled:opacity-40 transition-colors shadow-md">
              {sending
                ? <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                : <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
              }
            </button>
          </form>
        </div>
      </div>

      {/* ── Online users sidebar (desktop) ── */}
      <div className="hidden lg:flex flex-col w-52 bg-white border-l border-gray-100 flex-shrink-0">
        <div className="px-3 py-3 border-b border-gray-100">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Online Now</h3>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {onlineUsers.length===0
            ? <p className="text-xs text-gray-400 text-center mt-4 px-3">No one online yet</p>
            : onlineUsers.map((u,i)=>(
              <div key={i} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors">
                <div className="relative flex-shrink-0">
                  <div className="w-7 h-7 rounded-full bg-whatsapp-green flex items-center justify-center text-white text-xs font-bold">{getInitials(u.name)}</div>
                  <div className="absolute bottom-0 right-0 w-2 h-2 bg-green-400 rounded-full border border-white"/>
                </div>
                <span className="text-xs text-gray-700 truncate font-medium">{u.name}</span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

export default Chat;
